import React, { Dispatch, MouseEvent, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import {
  MetaFilterSelectedData,
  MetaFilterSelectedDate,
  MetaFilterTypes,
  NumbersRange,
  SearchFilterByRecent,
} from '@modules/search/types'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'

import { RECENT_LABELS } from '../constants'
import { MetaFilterButton, MetaFilterButtonGroup } from '../index.styles'
import DateRangeSelect from './DateRangeSelect'
import {
  MetaFilterDefaultTypography,
  MetaFilterPopoverCloseButton,
  MetaFilterPopoverItem,
  MetaFilterPopoverItemContent,
  MetaFilterPopoverItemContentInner,
  MetaFilterPopoverSubmitButton,
  MetaFilterStyledPopover,
  MetaFilterTypography,
} from './MetaFilterPopover.styles'

export interface MetaFilterPopoverProps {
  open: boolean
  id?: string
  handleClose: () => void
  anchorEl: Nullable<HTMLElement>
  dateRangeEnd: number
  dateRangeStart: number
  setDateRangeEnd: Dispatch<SetStateAction<number>>
  setDateRangeStart: Dispatch<SetStateAction<number>>
  eventsDatesRange: NumbersRange
  selectedFilter: MetaFilterSelectedData
  setMetaFilterType: Dispatch<SetStateAction<MetaFilterTypes>>
  handleSelectMetaFilter: (filterData: MetaFilterSelectedData) => void
  selectedType: MetaFilterTypes
  setSelectedType: Dispatch<SetStateAction<MetaFilterTypes>>
  isFilterByNewEnabled?: boolean
}

const MetaFilterPopover = (props: MetaFilterPopoverProps) => {
  const {
    open,
    id,
    handleClose,
    anchorEl,
    dateRangeEnd,
    dateRangeStart,
    setDateRangeEnd,
    setDateRangeStart,
    eventsDatesRange,
    selectedFilter,
    setMetaFilterType,
    handleSelectMetaFilter,
    selectedType,
    setSelectedType,
    isFilterByNewEnabled,
  } = props

  const { t } = useTranslation()

  const { from: selectedValueFrom, to: selectedValueTo } = selectedFilter.value as MetaFilterSelectedDate
  const [selectedDateStart, setSelectedDateStart] = useState(
    selectedFilter?.type === MetaFilterTypes.BY_DATE_RANGE && selectedValueFrom ? selectedValueFrom : dateRangeStart,
  )
  const [selectedDateEnd, setSelectedDateEnd] = useState(
    selectedFilter?.type === MetaFilterTypes.BY_DATE_RANGE && selectedValueTo ? selectedValueTo : dateRangeEnd,
  )

  const [selectedRecentButton, setSelectedRecentButton] = useState(
    selectedFilter?.type === MetaFilterTypes.BY_RECENT ? selectedFilter?.value : null,
  )

  const FILTER_VALUES_MAP = {
    [MetaFilterTypes.BY_DATE_RANGE]: { from: selectedDateStart, to: selectedDateEnd },
    [MetaFilterTypes.BY_EVENT]: '',
    [MetaFilterTypes.BY_RECENT]: selectedRecentButton || RECENT_LABELS[0].value,
    [MetaFilterTypes.BY_NEW_ITEMS]: 'NEW_ITEMS_PLACEHOLDER',
    [MetaFilterTypes.DEFAULT]: '',
  }

  const checkByType = (type: MetaFilterTypes) => {
    return selectedType === type
  }

  const handleSubmit = () => {
    if (checkByType(MetaFilterTypes.BY_DATE_RANGE)) {
      setDateRangeEnd(selectedDateEnd)
      setDateRangeStart(selectedDateStart)
    }

    handleSelectMetaFilter({ type: selectedType, value: FILTER_VALUES_MAP[selectedType] })
    setMetaFilterType(selectedType)
    handleClose()
  }

  const handleRecentButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    const target = e.target as HTMLInputElement
    setSelectedRecentButton(target.value)
    if (!checkByType(MetaFilterTypes.BY_RECENT)) {
      setSelectedType(MetaFilterTypes.BY_RECENT)
    }
  }

  const handleDefaultType = () => {
    if (!checkByType(MetaFilterTypes.DEFAULT)) {
      setSelectedType(MetaFilterTypes.DEFAULT)
    }
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target.value as MetaFilterTypes
    setSelectedType(target)

    if (target === MetaFilterTypes.BY_RECENT) {
      setSelectedRecentButton(SearchFilterByRecent.SEARCH_FILTER_BY_RECENT_WEEK)
    }
  }

  return (
    <MetaFilterStyledPopover
      id={id}
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      data-testid="metafilter-popover"
    >
      <MetaFilterPopoverCloseButton onClick={handleClose} endIcon={<CloseIcon />}>
        {t(translationKeys.SEARCH_FILTER_META_CLOSE)}
      </MetaFilterPopoverCloseButton>
      {isFilterByNewEnabled && (
        <MetaFilterPopoverItem $selected={checkByType(MetaFilterTypes.BY_NEW_ITEMS)}>
          <MetaFilterPopoverItemContent>
            <MetaFilterPopoverItemContentInner>
              <MetaFilterTypography>{t(translationKeys.NEW_ITEMS_LABEL)}</MetaFilterTypography>
              <Typography variant="body1">{t(translationKeys.NEW_ITEMS_DESCRIPTION)}</Typography>
            </MetaFilterPopoverItemContentInner>

            <Radio
              name="meta_filter"
              value={MetaFilterTypes.BY_NEW_ITEMS}
              checked={selectedType === MetaFilterTypes.BY_NEW_ITEMS}
              onChange={handleRadioChange}
            />
          </MetaFilterPopoverItemContent>
        </MetaFilterPopoverItem>
      )}

      <MetaFilterPopoverItem $selected={checkByType(MetaFilterTypes.BY_DATE_RANGE)}>
        <MetaFilterPopoverItemContent>
          <MetaFilterPopoverItemContentInner>
            <MetaFilterTypography>{t(translationKeys.SEARCH_FILTER_BY_DATE_RANGE)}</MetaFilterTypography>
            <DateRangeSelect
              selectedDateFrom={dateRangeStart}
              selectedDateTo={dateRangeEnd}
              eventsDatesRange={eventsDatesRange}
              setDateRangeSelectEnd={setSelectedDateEnd}
              setDateRangeSelectStart={setSelectedDateStart}
              setSelectedType={setSelectedType}
              isSelected={checkByType(MetaFilterTypes.BY_DATE_RANGE)}
            />
          </MetaFilterPopoverItemContentInner>
          <Radio
            name="meta_filter"
            value={MetaFilterTypes.BY_DATE_RANGE}
            checked={checkByType(MetaFilterTypes.BY_DATE_RANGE)}
            onChange={handleRadioChange}
          />
        </MetaFilterPopoverItemContent>
      </MetaFilterPopoverItem>
      <MetaFilterPopoverItem $selected={checkByType(MetaFilterTypes.BY_RECENT)} data-testid="meta-filter-recent-item">
        <MetaFilterPopoverItemContent>
          <MetaFilterPopoverItemContentInner>
            <MetaFilterTypography>{t(translationKeys.SEARCH_FILTER_BY_RECENT_LABEL)}</MetaFilterTypography>
            <MetaFilterButtonGroup>
              {RECENT_LABELS.map(button => (
                <MetaFilterButton
                  startIcon={selectedRecentButton === button.value ? <CheckIcon /> : null}
                  value={button.value}
                  onClick={handleRecentButtonClick}
                  key={button.value}
                  $active={selectedRecentButton === button.value}
                  data-testid={selectedRecentButton === button.value ? 'meta-filter-recent-active' : null}
                >
                  {t(button.label)}
                </MetaFilterButton>
              ))}
            </MetaFilterButtonGroup>
          </MetaFilterPopoverItemContentInner>
          <Radio
            name="meta_filter"
            value={MetaFilterTypes.BY_RECENT}
            checked={checkByType(MetaFilterTypes.BY_RECENT)}
            onChange={handleRadioChange}
          />
        </MetaFilterPopoverItemContent>
      </MetaFilterPopoverItem>
      <MetaFilterPopoverItem $selected={checkByType(MetaFilterTypes.DEFAULT)}>
        <MetaFilterPopoverItemContent onClick={handleDefaultType}>
          <MetaFilterDefaultTypography>{t(translationKeys.SEARCH_FILTER_BY_DATE_LABEL_EMPTY)}</MetaFilterDefaultTypography>
          <Radio
            name="meta_filter"
            value={MetaFilterTypes.DEFAULT}
            checked={checkByType(MetaFilterTypes.DEFAULT)}
            onChange={handleRadioChange}
          />
        </MetaFilterPopoverItemContent>
      </MetaFilterPopoverItem>
      <MetaFilterPopoverSubmitButton variant="contained" onClick={handleSubmit}>
        {t(translationKeys.SEARCH_FILTER_BY_DATE_SUBMIT)}
      </MetaFilterPopoverSubmitButton>
    </MetaFilterStyledPopover>
  )
}

export default MetaFilterPopover
